import React, { useState } from 'react';

type StoreStatusOption = 'Onboarding' | 'Active' | 'Churned' | 'Paused';

export interface StoreFormValues {
  // Section A: Financials
  planPrice?: string;
  monthlyFee?: string;
  affiliateAmount?: string;
  affiliatePartner?: string;
  totalValue?: string;

  // Section B: Client's Tasks
  contractSigned: boolean;
  dateSigned?: string;
  googleFormFilled: boolean;
  webshopReady: boolean;
  instructionsDone: boolean;

  // Section C: Our Tasks
  collabRequestSent: boolean;
  webshopGmcReady: boolean;
  gmcCreated: boolean;
  googleSetup: boolean;
  productsInReview: boolean;
  firstBatchApproved: boolean;
  dateFirstApproved?: string;
  dateGoodNewsSent?: string;
  storeStatus: string;

  // Section D: Invoicing Cycle
  stepPlanSent: boolean;
  dateStepPlan?: string;
  invoice1Sent: boolean;

  invoice2Date?: string;
  invoice2Sent: boolean;

  invoice3Date?: string;
  invoice3Sent: boolean;

  invoice4Date?: string;
  invoice4Sent: boolean;

  invoice5Date?: string;
  invoice5Sent: boolean;

  invoice6Date?: string;
  invoice6Sent: boolean;
}

interface AddStoreModalProps {
  clientName?: string;
  isOpen: boolean;
  initialValues?: Partial<StoreFormValues>;
  onClose: () => void;
  onSave?: (values: StoreFormValues) => void;
}

const defaultValues: StoreFormValues = {
  // Financials
  planPrice: '',
  monthlyFee: '',
  affiliateAmount: '',
  affiliatePartner: '',
  totalValue: '',

  // Client Tasks
  contractSigned: false,
  dateSigned: '',
  googleFormFilled: false,
  webshopReady: false,
  instructionsDone: false,

  // Our Tasks
  collabRequestSent: false,
  webshopGmcReady: false,
  gmcCreated: false,
  googleSetup: false,
  productsInReview: false,
  firstBatchApproved: false,
  dateFirstApproved: '',
  dateGoodNewsSent: '',
  storeStatus: 'Onboarding',

  // Invoicing
  stepPlanSent: false,
  dateStepPlan: '',
  invoice1Sent: false,

  invoice2Date: '',
  invoice2Sent: false,

  invoice3Date: '',
  invoice3Sent: false,

  invoice4Date: '',
  invoice4Sent: false,

  invoice5Date: '',
  invoice5Sent: false,

  invoice6Date: '',
  invoice6Sent: false,
};

const AddStoreModal: React.FC<AddStoreModalProps> = ({
  clientName,
  isOpen,
  initialValues,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState<StoreFormValues>({
    ...defaultValues,
    ...initialValues,
  });

  const statusOptions: StoreStatusOption[] = ['Onboarding', 'Active', 'Churned', 'Paused'];

  if (!isOpen) return null;

  const handleChange = (field: keyof StoreFormValues, value: string | boolean) => {
    setForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSave) {
      onSave(form);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 backdrop-blur-sm p-4">
      <div className="bg-white/95 border border-white/60 shadow-2xl rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white/80">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Add Store</h2>
            {clientName && (
              <p className="text-xs text-slate-500 mt-0.5">
                Linked to client <span className="font-medium text-slate-900">{clientName}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700 text-sm font-medium px-3 py-1 rounded-lg hover:bg-slate-100"
          >
            Close
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5 bg-slate-50/40">
          {/* Grid of 4 sections */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Financials */}
            <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Financials</h3>
                <span className="text-[10px] uppercase tracking-wide text-slate-400">
                  Plan & Fees
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Plan Price
                  </label>
                  <div className="flex items-center space-x-1">
                    <span className="text-xs text-slate-400">€</span>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                      value={form.planPrice ?? ''}
                      onChange={e => handleChange('planPrice', e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Monthly Fee
                  </label>
                  <div className="flex items-center space-x-1">
                    <span className="text-xs text-slate-400">€</span>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                      value={form.monthlyFee ?? ''}
                      onChange={e => handleChange('monthlyFee', e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Affiliate Amount
                  </label>
                  <div className="flex items-center space-x-1">
                    <span className="text-xs text-slate-400">€</span>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                      value={form.affiliateAmount ?? ''}
                      onChange={e => handleChange('affiliateAmount', e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Affiliate Partner
                  </label>
                  <input
                    type="text"
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                    value={form.affiliatePartner ?? ''}
                    onChange={e => handleChange('affiliatePartner', e.target.value)}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Total Value
                  </label>
                  <div className="flex items-center space-x-1">
                    <span className="text-xs text-slate-400">€</span>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                      value={form.totalValue ?? ''}
                      onChange={e => handleChange('totalValue', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Client Tasks */}
            <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Client Tasks</h3>
                <span className="text-[10px] uppercase tracking-wide text-slate-400">
                  Inputs from client
                </span>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-sm text-slate-700">Contract Signed</label>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                    checked={form.contractSigned}
                    onChange={e => handleChange('contractSigned', e.target.checked)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Date Signed
                  </label>
                  <input
                    type="date"
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                    value={form.dateSigned ?? ''}
                    onChange={e => handleChange('dateSigned', e.target.value)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">Google Form Filled</span>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                    checked={form.googleFormFilled}
                    onChange={e => handleChange('googleFormFilled', e.target.checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">Webshop Ready</span>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                    checked={form.webshopReady}
                    onChange={e => handleChange('webshopReady', e.target.checked)}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">Instructions Done</span>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                    checked={form.instructionsDone}
                    onChange={e => handleChange('instructionsDone', e.target.checked)}
                  />
                </div>
              </div>
            </section>

            {/* Our Tasks */}
            <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Our Tasks</h3>
                <span className="text-[10px] uppercase tracking-wide text-slate-400">
                  Internal pipeline
                </span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['collabRequestSent', 'Collab request sent'],
                  ['webshopGmcReady', 'Webshop GMC ready'],
                  ['gmcCreated', 'GMC created'],
                  ['googleSetup', 'Google setup done'],
                  ['productsInReview', 'Products in review'],
                  ['firstBatchApproved', 'First batch approved'],
                ].map(([field, label]) => (
                  <label
                    key={field}
                    className="flex items-center justify-between text-sm text-slate-700"
                  >
                    <span className="mr-2">{label}</span>
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                      checked={form[field as keyof StoreFormValues] as boolean}
                      onChange={e => handleChange(field as keyof StoreFormValues, e.target.checked)}
                    />
                  </label>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Date First Approved
                  </label>
                  <input
                    type="date"
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                    value={form.dateFirstApproved ?? ''}
                    onChange={e => handleChange('dateFirstApproved', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Date Good News Sent
                  </label>
                  <input
                    type="date"
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                    value={form.dateGoodNewsSent ?? ''}
                    onChange={e => handleChange('dateGoodNewsSent', e.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Store Status
                </label>
                <div className="flex items-center space-x-2">
                  <select
                    className="px-2 py-1.5 border border-slate-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                    value={statusOptions.includes(form.storeStatus as StoreStatusOption) ? form.storeStatus : 'Custom'}
                    onChange={e => {
                      const value = e.target.value;
                      if (value === 'Custom') return;
                      handleChange('storeStatus', value);
                    }}
                  >
                    {statusOptions.map(status => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                    <option value="Custom">Custom…</option>
                  </select>
                  <input
                    type="text"
                    placeholder="Custom status"
                    className="flex-1 px-2 py-1.5 border border-dashed border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                    value={form.storeStatus}
                    onChange={e => handleChange('storeStatus', e.target.value)}
                  />
                </div>
                <p className="mt-1 text-[11px] text-slate-400">
                  Suggestions: Onboarding, Active, Churned, Paused.
                </p>
              </div>
            </section>

            {/* Invoicing Pipeline */}
            <section className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">Invoicing Pipeline</h3>
                <span className="text-[10px] uppercase tracking-wide text-slate-400">
                  Steps 1–6
                </span>
              </div>
              <div className="space-y-3">
                {/* Step plan */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-700">
                    Step Plan Sent <span className="text-xs text-slate-400">(Invoice 1 trigger)</span>
                  </span>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                    checked={form.stepPlanSent}
                    onChange={e => handleChange('stepPlanSent', e.target.checked)}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600 mb-1">
                    Date Step Plan
                  </label>
                  <input
                    type="date"
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                    value={form.dateStepPlan ?? ''}
                    onChange={e => handleChange('dateStepPlan', e.target.value)}
                  />
                </div>

                {/* Invoice 1 */}
                <div className="flex items-center justify-between pt-1 border-t border-slate-100 mt-2">
                  <span className="text-sm text-slate-700">Invoice 1 Sent</span>
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                    checked={form.invoice1Sent}
                    onChange={e => handleChange('invoice1Sent', e.target.checked)}
                  />
                </div>

                {/* Invoice 2–6 grid */}
                <div className="grid grid-cols-2 gap-3 mt-1">
                  {[2, 3, 4, 5, 6].map(n => {
                    const dateKey = `invoice${n}Date` as keyof StoreFormValues;
                    const sentKey = `invoice${n}Sent` as keyof StoreFormValues;
                    return (
                      <div
                        key={n}
                        className="border border-slate-100 rounded-xl p-2.5 bg-slate-50/40 space-y-2"
                      >
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-slate-700">
                            Invoice {n}
                          </span>
                          <input
                            type="checkbox"
                            className="h-3.5 w-3.5 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                            checked={form[sentKey] as boolean}
                            onChange={e => handleChange(sentKey, e.target.checked)}
                          />
                        </div>
                        <input
                          type="date"
                          className="w-full px-2 py-1.5 border border-slate-200 rounded-lg text-xs focus:ring-2 focus:ring-slate-500/20 focus:outline-none"
                          value={(form[dateKey] as string) ?? ''}
                          onChange={e => handleChange(dateKey, e.target.value)}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-white/90 flex items-center justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            formAction=""
            onClick={handleSubmit}
            className="px-5 py-2 text-sm font-medium text-white bg-slate-900 rounded-lg hover:bg-slate-800 transition-colors shadow-sm"
          >
            Save Store
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddStoreModal;


